import { LocalStorageService } from "src/app/kernel/tools/services/local-storage.service";
import { Injectable } from "@angular/core";
import { BehaviorSubject, first, map } from "rxjs";
import { GraphService } from "src/app/kernel/graphql/services/graph.service";
import { Router } from "@angular/router";
import {IBaseUser} from "../../shared/interfaces/user.interface";
import {IAuthenticationResult, IUnsecureAuthenticationResult} from "../interfaces/authentication.interface";
import {ILoginDto} from "../interfaces/login.interface";
import {AuthUserModel} from "../models/auth-user.model";

@Injectable({
  providedIn: "root",
})
export class AuthService {
  public user$ = new BehaviorSubject<AuthUserModel | undefined>(undefined);
  public token$ = new BehaviorSubject<string | undefined>(undefined);

  userTokenTypeSelection: string[] = [
    "accessType",
    "token",
    "user.id",
    "user.name",
    "user.surname",
    "user.email",
    "user.mobile",
    "user.avatar",
    "user.gender",
    "user.birthDate",
    "user.createdAt",
    "user.code"
  ];

  constructor(
    private graphService: GraphService,
    private localStorageService: LocalStorageService,
    private router: Router,
  ) {
    this.setRawUser(this.localStorageService.get<IBaseUser>("user"));
    this.setToken(this.localStorageService.get<string>("token") ?? undefined);
  }

  /**
   * Executes the login mutation.
   * @param loginData
   */
  login(loginData: ILoginDto) {
    return this.graphService
      .constructMutation<{ adminLogin: IAuthenticationResult }>(
        "adminLogin",
        { input: "AccessCredentialsInput!" },
        { input: loginData },
        this.userTokenTypeSelection
      )
      .pipe(
        first(),
        map((res) => ({ ...res, data: res.data?.adminLogin }))
      );
  }

  /**
   * Executes the 2FA verification mutation.
   * @param otp
   */
  verify2FA(otp: string) {
    return this.graphService
      .constructMutation<{ verifyAdmin2FACode: IAuthenticationResult }>(
        "verifyAdmin2FACode",
        { otp: "String!" },
        { otp: otp },
        this.userTokenTypeSelection
      )
      .pipe(
        first(),
        map((res) => ({ ...res, data: res.data?.verifyAdmin2FACode }))
      );
  }

  /**
   * Executes the resend OTP mutation.
   */
  resend2FACode() {
    return this.graphService
      .constructMutation<{ resendOtp: { text: string; code: number } }>("resendAdmin2FACode", {}, {}, ["text", "code"])
      .pipe(
        first(),
        map((res) => ({ ...res, data: res.data?.resendOtp }))
      );
  }

  /**
   * Executes the init reset password mutation.
   */
  initResetPassword(email: string) {
    return this.graphService
      .constructMutation<{ initResetAdminPassword: IUnsecureAuthenticationResult }>(
        "initResetAdminPassword",
        {email: 'String!'},
        {email},
        ["accessType", "token",]
      )
      .pipe(
        first(),
        map((res) => ({ ...res, data: res.data?.initResetAdminPassword }))
      );
  }

  /**
   * Executes the Reset Password verification mutation.
   * @param otp
   */
  verifyResetPassword(otp: string) {
    return this.graphService
      .constructMutation<{ verifyRestPassword: IUnsecureAuthenticationResult }>(
        "verifyRestPassword",
        { otp: "String!" },
        { otp: otp },
        ["accessType", "token",]
      )
      .pipe(
        first(),
        map((res) => ({ ...res, data: res.data?.verifyRestPassword }))
      );
  }

  /**
   * Executes the resend reset password OTP mutation.
   */
  resendResetPasswordOtp() {
    return this.graphService
      .constructMutation<{ resendResetPasswordOtp: { text: string; code: number } }>(
        "resendResetPasswordOtp",
        {},
        {},
        ["text", "code"]
      ).pipe(
        first(),
        map((res) => ({ ...res, data: res.data?.resendResetPasswordOtp }))
      );
  }

  /**
   * Executes the confirm reset password mutation.
   */
  confirmResetPassword(password: string) {
    return this.graphService
      .constructMutation<{ confirmResetPassword: { text: string; code: number } }>(
        "confirmResetPassword",
        {password: "String!"},
        {password},
        ["text", "code"]
      ).pipe(
        first(),
        map((res) => ({ ...res, data: res.data?.confirmResetPassword }))
      );
  }

  /**
   * Authenticates the user with the provided authentication result.
   * @param authenticationResult
   */
  authenticateUser(authenticationResult: IAuthenticationResult) {
    this.setToken(authenticationResult.token);
    this.setRawUser(authenticationResult.user);
  }

  /**
   * Partially authenticates the user for specific operations like reset password.
   * @param authenticationResult
   */
  partialAuthenticateUser(authenticationResult: IUnsecureAuthenticationResult) {
    this.setToken(authenticationResult.token);
  }


  /**
   * Sets the user auth token.
   * @param token
   */
  setToken(token: string | undefined): AuthService {
    this.token$.next(token);
    this.localStorageService.set("token", token);
    return this;
  }

  /**
   * Sets the current authenticated user.
   * @param user
   */
  setUser(user: AuthUserModel | undefined): AuthService {
    this.user$.next(user);
    this.localStorageService.set("user", user);
    return this;
  }

  /**
   * Sets the current authenticated user from a raw user object.
   * @param user
   */
  setRawUser(user: IBaseUser | null): AuthService {
    this.setUser(user ? AuthUserModel.create(user) : undefined);
    return this;
  }

  /**
   * Returns the current authenticated user.
   */
  getUser(): AuthUserModel | undefined {
    return this.user$.value;
  }

  /**
   * Returns the user auth token.
   */
  getToken(): string | undefined {
    return this.token$.value;
  }

  /**
   * Returns the user auth token.
   */
  clearUser() {
    this.setUser(undefined).setToken(undefined);
    this.localStorageService.remove("user").remove("token");
  }

  /**
   * Logs the user out.
   */
  logout() {
    this.clearUser();
    return this.router.navigate(["/auth/login"]);
  }

}
